import axios, { AxiosInstance } from 'axios'
import {
    AssessmentDTO,
    ErrorPayload,
    OCODES,
    OCODES_TYPE,
    ProfileData,
    SUBCODES,
    Scanning360Request,
} from './types'
import Cookies from 'js-cookie'
import { info, logException } from './sentryLogger'
import { invertRanking, nullifyUnassigned } from './utils'
import {
    removeLoginData,
    installLoginData,
    decodeJwt,
    COOKIE_TOKEN,
    COOKIE_EMAIL,
} from './helpers'
import { redirect, rewriteLocation } from '../hooks/useURLParams'
import { ALL_SLUGS } from '../parameters/clients'
import { ParamsDTO } from '../parameters/types'
import { DEFAULT_AUTH_STATE } from '../context/defaultState'
import { isApiDisabled } from './envService'
import _ from 'lodash'
import { defaultParamsFitme, TEST_SLUG } from '../parameters/defaultParamsFitme'
import { resetSession } from '../context/session/helpers'
import { fromBCP47toLegacy } from '../i18n/bcp47toLegacy'
import { ALL_LANGUAGES_TYPE } from '../i18n/constants'

export const API_URL_STAGING = 'https://serve.staging.twegos.com/export'
// FIXME WAS https://st_assessment2.fitme.jobs/export
const CORS_PROXY = 'https://cors-anywhere.herokuapp.com/'
export const API_URL_DEV = 'https://serve.dev.twegos.com/export'
export const API_URL_PROD = 'https://serve.fitme.jobs/export'

export const apiUrl = (() => {
    switch (window.location.origin) {
        case 'https://assessment-3.twegos.com':
            // SIGNUP : https://recruit.twegos.com/SLUG/SOURCE
            // LOGIN : https://assessment-3.twegos.com/
            // CONTROL : https://control.twegos.com
            // CLIENT : https://clientcenter.twegos.com/SLUG/SOURCE
            return API_URL_PROD
        case 'https://stage-assessment-3.twegos.com':
            // SIGNUP : https://recruit.staging.twegos.com/SLUG/SOURCE
            // LOGIN : https://stage-assessment-3.twegos.com/
            // CONTROL : https://control.staging.twegos.com
            // CLIENT : https://clientcenter.staging.twegos.com/SLUG/SOURCE
            return API_URL_STAGING
        case 'https://test-assessment-3.twegos.com':
        case 'http://localhost:3000':
        case 'http://localhost:3001':
        case 'http://127.0.0.1:3000':
        default:
            // SIGNUP : https://recruit.dev.twegos.com/SLUG/SOURCE
            // LOGIN : https://test-assessment-3.twegos.com/
            // CONTROL : https://control.dev.twegos.com
            // CLIENT : https://clientcenter.dev.twegos.com/SLUG/SOURCE
            return API_URL_DEV
    }
})();
console.debug('>>>>>>> apiUrl', apiUrl) // FIXME remove

export const authApi: AxiosInstance = axios.create({
    baseURL: apiUrl,
    headers: {
        'Content-Type': 'application/json',
    },
})

let intervalID: NodeJS.Timeout | null = null
export const clearTokenRenewal = () => intervalID && clearInterval(intervalID)
export const startTokenRenewal = () => {
    info('startTokenRenewal()')
    clearTokenRenewal()
    intervalID = setInterval(async () => {
        info('Renew token')
        await apiService.renewToken()
    }, 1000 * 3600)
}

export const apiService = {
    logout: async (path: string = '/') => {
        info('logout')
        removeLoginData(authApi)
        if (path.includes('http')) {
            redirect(path)
        } else {
            rewriteLocation(path)
            window.location.reload()
        }
    },
    fetchLoginState: async (
        initialLoginState: Partial<typeof DEFAULT_AUTH_STATE> = {}
    ) => {
        info('fetchLoginState()', initialLoginState)
        let { token, slug, source, from } = initialLoginState
        if (isApiDisabled()) {
            return {
                ...initialLoginState,
                // slug: slug && slug in clientParameters ? slug : TEST_SLUG,
                // slug,
            } as Required<typeof DEFAULT_AUTH_STATE>
        }
        const profile: ProfileData = await apiService.profile()
        if (slug && slug != profile.currentCompanySlug) {
            await apiService.updateSlug(slug)
        } else {
            if (profile.currentCompanySlug) {
                slug = profile.currentCompanySlug as ALL_SLUGS
            } else {
                logException('fetchLoginState', 'No slug')
                slug = TEST_SLUG as ALL_SLUGS
            }
        }
        if (source && profile.currentSource !== source) {
            await apiService.updateSource(source)
        } else {
            source = profile.currentSource
        }
        ;
        const res = {
            token,
            slug: slug ?? profile.currentCompanySlug,
            source: source ?? profile.currentSource,
            from,
            userEmail: profile.email ?? initialLoginState.userEmail,
            userId: profile.sub ?? initialLoginState.userId,
        } as Required<typeof DEFAULT_AUTH_STATE>
        return res;
    },
    installToken: (email?: string, token?: string) => {
        email = email ?? Cookies.get(COOKIE_EMAIL)
        token = token ?? Cookies.get(COOKIE_TOKEN)
        if (token) {
            const decoded = decodeJwt(token)
            if (decoded && decoded.exp && decoded.exp * 1000 < Date.now()) {
                info('installToken', 'Token expired')
                removeLoginData(authApi)
                throw new Error('Token expired')
            } else {
                return installLoginData(authApi, token, email)
            }
        } else {
            info('installToken', 'No token')
            return ''
        }
    },
    loginWithAB: async (email: string, a: string, b: string) => {
        info('loginWithAB', email, a, b)
        const { access_token } = (
            await authApi.post(
                'authc/confirm_signup',
                {},
                { headers: { ecth: a, reqi: b } }
            )
        )?.data
        return access_token as string
    },
    login: async (email: string, password: string) => {
        if (isApiDisabled()) return 'API_DISABLED'
        const { access_token } = (
            await authApi.post('authc/login', { email, password })
        )?.data
        info('login', access_token)
        return installLoginData(authApi, access_token, email)
    },
    renewToken: async () => {
        try {
            info('renewToken()', Cookies.get(COOKIE_TOKEN))
            const { access_token } = (await authApi.post('/authc/renew'))?.data
            info(
                'login; old=',
                Cookies.get(COOKIE_TOKEN),
                'new=',
                access_token,
                decodeJwt(access_token)
            )
            return installLoginData(authApi, access_token)
        } catch (error: any) {
            info('renewToken() error', JSON.stringify(error))
            if (
                // axios.isAxiosError(error) && // FIXME this is false, even for 401
                error?.status === OCODES.UNAUTHORIZED.status ||
                error?.response?.status === OCODES.UNAUTHORIZED.status ||
                error?.message?.includes('401')
            ) {
                logException(
                    'renewToken',
                    'Unauthorized - auto logout()',
                    error
                )
                apiService.logout(defaultParamsFitme.brandParams.returnUrl)
                window.location.reload() //FIXME
            } else {
                logException('renewToken', 'unknown', error)
                throw new Error(OCODES.UNKNOWN.key)
            }
        }
    },
    isAlive: async () => {
        const alive = (await authApi.get('alive-x'))?.data
        info('isAlive()', { alive })
        return alive
    },
    profile: async () => {
        /*
            currentCompanySlug
            email
            exp
            iat
            sub
            */
        const profile = (await authApi.get('authc/profile'))?.data
        info('profile()', { profile })
        return profile as ProfileData
    },
    updateSlug: async (currentCompanySlug: string) => {
        const res = await authApi.put('/authc/currentCompanySlug', {
            currentCompanySlug,
        })
        info('slug()', { res })
        return res
    },
    updateSource: async (currentSource: string) => {
        info('updateSource()', currentSource)
        try {
            // FIXME clickup
            const res = await authApi.put('/authc/currentSource', {
                currentSource,
            })
            info('updateSource()', { res })
            return res
        } catch (error: any) {
            logException('updateSource', 'Error updating source', error)
            return false
        }
    },
    downloadReport: async (language: ALL_LANGUAGES_TYPE) => {
        const legacyLanguage = fromBCP47toLegacy(language);
        // FIXME @login if has report, test this and show thanks
        const reportBlob: any = (
            await authApi.get(`assessment/report`, {
                params: {
                    language: legacyLanguage,
                },
                headers: {
                    'Content-Type': 'application/pdf',
                    // 'Accept': 'application/pdf', TODO 400 Bad Request
                },
                responseType: 'blob',
            })
        )?.data
        return reportBlob
    },
    publishAssessment: async (
        assessment: AssessmentDTO,
        isQSortV2: boolean = false
    ): Promise<[OCODES_TYPE, SUBCODES]> => {
        // Invert only if BinarySort (not QSortV2)
        // - The binarySort always required an inversion (FIXME algorithm for later)
        // - The qsortV2 was not implemented correctly from the start, it had a faulty inversion : fromQSortV2 was always false and was not used not skip invertRanking()
        // With the 2nd faulty commit of 26th of April, we ceased any inversion.
        // - we were never inverting the ranking for QSortV2 and Binary sort
        // - So this 2nd bug was fixing the QSortV2 and breaking the BinarySort
        assessment = isQSortV2 ? assessment : invertRanking(assessment)
        assessment = nullifyUnassigned(assessment)
        info('publishAssessment()', assessment)
        info(
            'publishAssessment()',
            Object.keys(assessment)
                // @ts-ignore
                .toSorted()
                // @ts-ignore
                .map((k) => `${k} ${assessment[k]}`)
        )
        try {
            const response = await authApi.post(
                'assessment/values',
                assessment,
                {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }
            )
            // FIXME {"result":1} response.data?.result === 1 && response.status === 200 ?
            if (response?.status === 200 && response?.data?.result === 1) {
                resetSession()
                return [OCODES.OK.key, 'OK']
            }
            info('publishAssessment() 200', response.status, response.data)
            return [OCODES.OK.key, 'OK']
        } catch (error: any) {
            if (axios.isAxiosError(error)) {
                info('publishAssessment() error axios: ', error)
                let { code, status, message, name, response } = error //FIXME unused
                // response contains : data, status
                const errPayload = response?.data as ErrorPayload
                code = errPayload?.error?.code ?? code
                message = errPayload?.error?.message ?? message
                status = response?.status ?? status
                info('publishAssessment()', {
                    code,
                    status,
                    message,
                    name,
                })
                if (status === OCODES.UNAUTHORIZED.status) {
                    logException('publishAssessment', 'Unauthorized', error)
                    return [OCODES.UNAUTHORIZED.key, code as SUBCODES]
                }
                if (status === OCODES.BAD_REQUEST.status) {
                    logException('publishAssessment', 'Bad request', error)
                    return [OCODES.BAD_REQUEST.key, code as SUBCODES]
                }
                // FIXME useless
                if (code === OCODES.DUPLICATE_ENTRY.code) {
                    logException('publishAssessment', 'Duplicate entry', error)
                    return [OCODES.DUPLICATE_ENTRY.key, code as SUBCODES]
                }
                if (status === OCODES.INTERNAL_SERVER_ERROR.status) {
                    logException(
                        'publishAssessment',
                        'Internal server error',
                        error
                    )
                    return [OCODES.INTERNAL_SERVER_ERROR.key, 'ERROR']
                }
            } else {
                logException('publishAssessment', 'unknown', error)
                throw new Error(OCODES.UNKNOWN.key)
            }
        }
        return [OCODES.UNKNOWN.key, 'ERROR']
    },
    sendScanningReport: async (req: Scanning360Request) => {
        info('sendScanningReport()', req)
        try {
            const res = await axios.post(
                'https://scanning360.exoconsult.com/v1/form/answers?',
                req
            )
            info('sendScanningReport() res', res)
            if (res.status === 200) {
                return true
            } else {
                throw new Error('Error sending scanning report (status != 200)')
            }
        } catch (error: any) {
            const res = await axios.post(
                'https://scanning360.exoconsult.com/v1/form/answers?',
                _.omit(req, ['userAgent', 'language'])
            )
            if (res.status === 200) {
                return true
            }
            logException(
                'sendScanningReport',
                'Error sending scanning report',
                error
            )
            return false
        }
    },
    /**
     * https://app.clickup.com/9012002895/v/dc/8cjgg2f-1172/8cjgg2f-1652
     * https://clientcenter.twegos.com/en-en/company/brandpage
     * https://clientcenter.twegos.com/en-en/source/overview
     * @param slug
     * @param source
     * @returns
     */
    fetchParameters: async (
        slug: string,
        source?: string
    ): Promise<ParamsDTO | {}> => {
        const sourceQuery = source ? `&sourceSlug=${source}` : ''
        try {
            const theParams = (
                await authApi.get(
                    `authc/params?companySlug=${slug}${sourceQuery}`
                )
            )?.data as ParamsDTO
            return theParams
        } catch (error: any) {
            // throws 400 (message=PARAMS_NOT_FOUND) Incorrect sourceSlug or companySlug or missing
            /*
            message: "PARAMS_NOT_FOUND"
            statusCode: 400*/
            // source optionnal, reçoit pas les assessement params
            logException('fetchParameters', 'Error fetching parameters', error)
            throw error // FIXME
            // return {}
        }
    },
}
